import type { AxiosResponse } from 'axios';
import axios from 'axios';
import dayjs from 'dayjs';
import { QueryClient } from 'react-query';
import * as Yup from 'yup';
import HttpServiceHelper from './httpServiceHelper';
import { menuImportBatchesApi, menuImportQuotaApi, menuImportUploadsApi } from '../constants/apiEndPoints';
import { DATE_FORMAT } from '../constants/constants';
import { _HttpMethods } from '../constants/staticTypes';

// ---------------------------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------------------------

export type _MenuImportStatus =
    | 'UPLOADED'
    | 'SPLITTING'
    | 'EXTRACTING'
    | 'CONSOLIDATING'
    | 'REVIEW_PENDING'
    | 'APPROVED'
    | 'COMMITTING'
    | 'PARTIALLY_COMMITTED'
    | 'PARTIALLY_PUBLISHED'
    | 'COMPLETED'
    | 'FAILED'
    | 'CANCELLED';

export type _MenuImportMimeType = 'application/pdf' | 'image/png' | 'image/jpeg' | 'image/webp';

export type _MenuImportFileMeta = {
    fileName: string;
    mimeType: _MenuImportMimeType;
    sizeBytes: number;
};

export type _MenuImportUploadForm = {
    fileName: string;
    fileKey: string;
    url: string;
    fields: Record<string, string>;
    expiresAt: string;
};

export type _MenuImportCreateBody = {
    files: (_MenuImportFileMeta & { fileKey: string })[];
    targetMenuName?: string;
    acknowledgePreviousImport?: boolean;
    replacesBatchId?: string;
};

export type _DraftNodeMeta = {
    nodeId?: string;
    selected?: boolean;
    readonly committed?: boolean;
    readonly entityId?: string | null;
};

export type _DraftPrice = {
    fieldName: string;
    fieldValue: number | string;
    isDefault: boolean;
};

export type _DraftItem = {
    itemName: string;
    itemDescription?: string;
    itemType: 'food' | 'beverage';
    itemPrices: _DraftPrice[];
    isMarketPrice?: boolean;
    itemTag?: string;
    isAvailable?: boolean;
    confidence: 'high' | 'medium' | 'low';
    sourceRef: unknown | null;
} & _DraftNodeMeta;

export type _DraftCategory = {
    categoryName: string;
    categoryDescription?: string;
    items: _DraftItem[];
} & _DraftNodeMeta;

export type _DraftMenu = {
    menuName: string;
    categories: _DraftCategory[];
} & _DraftNodeMeta;

export type _DraftTree = {
    draftSchemaVersion: number;
    menus: _DraftMenu[];
};

export type _MenuImportSourceFile = {
    fileName: string;
    fileKey?: string;
    mimeType?: _MenuImportMimeType;
    sizeBytes?: number;
    previewUrl?: string | null;
    pageCount: number | null;
};

export type _MenuImportActor = {
    userId: string;
    userType: string;
};

export type _MenuImportPublishedBy = {
    userId: string;
    at: string;
};

export type _MenuImportFinishedBy = {
    userId: string;
    at?: string;
};

export type _MenuImportBatchSummary = {
    id: string;
    status: _MenuImportStatus;
    progress: { processedChunks: number; totalChunks: number; totalPages: number };
    targetMenuName: string | null;
    fileCount: number;
    regenerationCount: number;
    createdBy: _MenuImportActor | null;
    createdAt: string;
    finishedAt: string | null;
    cancelReason: string | null;
    supersededByBatchId: string | null;
    replacesBatchId: string | null;
};

export type _MenuImportBatch = _MenuImportBatchSummary & {
    sourceFiles: _MenuImportSourceFile[];
    draftTree: _DraftTree | null;
    draftVersion: number;
    results: { menus: number; categories: number; items: number };
    errors: unknown[];
    approvedBy: _MenuImportActor | null;
    publishedBy: _MenuImportPublishedBy | null;
    finishedBy?: _MenuImportFinishedBy | null;
    startedAt: string | null;
    supersededBatchIds?: string[];
    usage?: Record<string, unknown>;
};

export type _MenuImportEnvelope<T> = {
    code: string;
    message: string;
    data: T;
    details: unknown;
};

export type _MenuImportListEnvelope = _MenuImportEnvelope<_MenuImportBatchSummary[]> & {
    details: { pagination: { nextCursor: string | null; hasMore: boolean } };
};

export type _MenuImportErrorKind =
    | 'previousImportExists'
    | 'importInProgress'
    | 'concurrentLimit'
    | 'monthlyLimit'
    | 'limitReached'
    | 'committedNodeLocked'
    | 'invalidNodeId'
    | 'conflict'
    | 'badRequest'
    | 'validation'
    | 'notFound'
    | 'unavailable'
    | 'network'
    | 'unknown';

export type _MenuImportBatchRef = {
    id: string;
    status: _MenuImportStatus;
    targetMenuName?: string;
    finishedAt?: string | null;
};

export type _MenuImportErrorData = {
    previousBatch?: _MenuImportBatchRef;
    blockingBatch?: _MenuImportBatchRef;
    activeBatches?: _MenuImportBatchRef[];
    nodeIds?: string[];
    limitType?: string;
    limit?: number;
    remaining?: number;
    resetsAt?: string;
};

export type _MenuImportError = {
    kind: _MenuImportErrorKind;
    status?: number;
    message?: string;
    data?: _MenuImportErrorData;
    details?: unknown;
};

export type _MenuImportBatchResponse = AxiosResponse<_MenuImportEnvelope<_MenuImportBatch>>;

export type _MenuImportUploadsResponse = AxiosResponse<_MenuImportEnvelope<{ uploads: _MenuImportUploadForm[] }>>;

export type _MenuImportDraftResponse = AxiosResponse<_MenuImportEnvelope<{ draftVersion: number; draftTree: _DraftTree }>>;

export type _ImportCount = {
    used: number;
    limit: number;
    remaining: number;
};

export type _MenuImportQuota = {
    pagesLimit: number;
    pagesUsed: number;
    pagesRemaining: number;
    periodStart: string;
    periodEnd: string;
    regenerationLimit: number;
    concurrentLimit: number;
    isUnlimited: boolean;
    source: string;
    importAllowance: {
        period: string;
        periodStart: string;
        periodEnd: string;
        resetsAt: string;
        location: _ImportCount;
        organization: _ImportCount & { source: string };
    };
};

export type _MenuImportQuotaResponse = AxiosResponse<_MenuImportEnvelope<_MenuImportQuota>>;

/** The organization-level monthly import allowance shown on the upload and review steps. */
export type _OrgImportQuota = _ImportCount & { resetsAt: string };

// ---------------------------------------------------------------------------------------------
// Request functions
// ---------------------------------------------------------------------------------------------

export const createUploadForms = (
    queryClient: QueryClient,
    venueId: string,
    files: _MenuImportFileMeta[]
): Promise<_MenuImportUploadsResponse> =>
    HttpServiceHelper({
        url: menuImportUploadsApi,
        method: _HttpMethods.POST,
        queryClient,
        xTenantId: venueId,
        data: { files },
    });

// The pre-signed S3 POST must not carry the app's auth or tenant headers. HttpServiceHelper merges its
// defaults first and the payload headers last, and axios drops a header whose value is false.
export const uploadFileToS3 = (form: _MenuImportUploadForm, file: File): Promise<AxiosResponse<unknown>> => {
    const data = new FormData();
    Object.entries(form.fields).forEach(([key, value]) => data.append(key, value));
    data.append('file', file);

    return HttpServiceHelper({
        url: form.url,
        method: _HttpMethods.POST,
        isSignedUrl: true,
        noAuth: true,
        headers: {
            'Content-Type': false,
            'Cache-Control': false,
            'X-Admin-Id': false,
            'X-Organization-Id': false,
            'X-Tenant-Id': false,
        },
        data,
    });
};

export const createBatch = (queryClient: QueryClient, venueId: string, body: _MenuImportCreateBody): Promise<_MenuImportBatchResponse> =>
    HttpServiceHelper({
        url: menuImportBatchesApi,
        method: _HttpMethods.POST,
        queryClient,
        xTenantId: venueId,
        data: body,
    });

export const getBatches = (
    queryClient: QueryClient,
    venueId: string,
    params: { limit: number; cursor?: string }
): Promise<AxiosResponse<_MenuImportListEnvelope>> => {
    const query = [`limit=${params.limit}`];
    if (params.cursor) {
        query.push(`cursor=${encodeURIComponent(params.cursor)}`);
    }

    return HttpServiceHelper({
        url: `${menuImportBatchesApi}?${query.join('&')}`,
        method: _HttpMethods.GET,
        queryClient,
        xTenantId: venueId,
    });
};

export const getBatch = (queryClient: QueryClient, venueId: string, batchId: string): Promise<_MenuImportBatchResponse> =>
    HttpServiceHelper({
        url: `${menuImportBatchesApi}/${batchId}`,
        method: _HttpMethods.GET,
        queryClient,
        xTenantId: venueId,
    });

export const getImportQuota = (queryClient: QueryClient, venueId: string): Promise<_MenuImportQuotaResponse> =>
    HttpServiceHelper({
        url: menuImportQuotaApi,
        method: _HttpMethods.GET,
        queryClient,
        xTenantId: venueId,
    });

/** Formats an ISO instant as its UTC calendar date (the date part is not shifted into local time). */
export const formatUtcDate = (isoInstant: string) => dayjs(isoInstant.slice(0, 10)).format(DATE_FORMAT);

/** A file size for display: bytes below 1 KB, then KB or MB with one decimal. */
export const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export type _QuotaLevel = 'ok' | 'low' | 'exhausted';

/** How much of the allowance is left: nothing (or no limit), 25% or less, or more than that. */
export const getQuotaLevel = (remaining: number, limit: number): _QuotaLevel => {
    if (limit <= 0 || remaining <= 0) {
        return 'exhausted';
    }

    return remaining / limit <= 0.25 ? 'low' : 'ok';
};

/** Reads only the organization allowance and the reset date; venue-level figures are not shown. */
export const getOrgImportQuota = (quota?: _MenuImportQuota): _OrgImportQuota | null => {
    const allowance = quota?.importAllowance;

    // Without a reset date the summary cannot be written in full, so it reads as unavailable.
    if (!allowance?.organization || !allowance.resetsAt) {
        return null;
    }

    const { used, limit, remaining } = allowance.organization;

    return { used, limit, remaining, resetsAt: allowance.resetsAt };
};

export const updateDraft = (
    queryClient: QueryClient,
    venueId: string,
    batchId: string,
    draftVersion: number,
    draftTree: _DraftTree
): Promise<_MenuImportDraftResponse> =>
    HttpServiceHelper({
        url: `${menuImportBatchesApi}/${batchId}/draft`,
        method: _HttpMethods.PUT,
        queryClient,
        xTenantId: venueId,
        data: { draftVersion, draftTree },
    });

const postBatchAction = (
    queryClient: QueryClient,
    venueId: string,
    batchId: string,
    action: string,
    data?: {}
): Promise<_MenuImportBatchResponse> =>
    HttpServiceHelper({
        url: `${menuImportBatchesApi}/${batchId}/${action}`,
        method: _HttpMethods.POST,
        queryClient,
        xTenantId: venueId,
        ...(data ? { data } : {}),
    });

export const publishBatch = (queryClient: QueryClient, venueId: string, batchId: string): Promise<_MenuImportBatchResponse> =>
    postBatchAction(queryClient, venueId, batchId, 'publish');

export const finishBatch = (queryClient: QueryClient, venueId: string, batchId: string): Promise<_MenuImportBatchResponse> =>
    postBatchAction(queryClient, venueId, batchId, 'finish');

export const resumeBatch = (queryClient: QueryClient, venueId: string, batchId: string): Promise<_MenuImportBatchResponse> =>
    postBatchAction(queryClient, venueId, batchId, 'resume');

export const cancelBatch = (queryClient: QueryClient, venueId: string, batchId: string): Promise<_MenuImportBatchResponse> =>
    postBatchAction(queryClient, venueId, batchId, 'cancel');

export const regenerateBatch = (queryClient: QueryClient, venueId: string, batchId: string): Promise<_MenuImportBatchResponse> =>
    postBatchAction(queryClient, venueId, batchId, 'regenerate', {});

// ---------------------------------------------------------------------------------------------
// Error classifier
// ---------------------------------------------------------------------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

const withStatus = (
    kind: _MenuImportErrorKind,
    status: number,
    message: string | undefined,
    data?: _MenuImportErrorData,
    details?: unknown
): _MenuImportError => ({
    kind,
    status,
    ...(message !== undefined ? { message } : {}),
    ...(data ? { data } : {}),
    ...(details !== undefined ? { details } : {}),
});

/**
 * Maps a failed menu-import request to a kind the UI can branch on. The backend sends no machine-readable
 * error code, so the kind comes from the HTTP status plus the shape of `response.data.data`.
 */
export const classifyMenuImportError = (error: unknown): _MenuImportError => {
    if (!axios.isAxiosError(error)) {
        return { kind: 'unknown' };
    }

    const response: unknown = error.response;
    if (!isRecord(response) || typeof response.status !== 'number') {
        return { kind: 'network', ...(error.message ? { message: error.message } : {}) };
    }

    const { status } = response;
    const body = isRecord(response.data) ? response.data : undefined;
    const message = body && typeof body.message === 'string' ? body.message : undefined;
    const payload = body && isRecord(body.data) ? (body.data as _MenuImportErrorData) : undefined;
    const has = (key: keyof _MenuImportErrorData) => payload !== undefined && payload[key] !== undefined && payload[key] !== null;

    switch (status) {
        case 409:
            if (has('previousBatch')) return withStatus('previousImportExists', status, message, payload);
            if (has('blockingBatch')) return withStatus('importInProgress', status, message, payload);
            if (has('nodeIds')) return withStatus('committedNodeLocked', status, message, payload);
            return withStatus('conflict', status, message);
        case 429:
            if (has('activeBatches')) return withStatus('concurrentLimit', status, message, payload);
            if (has('limitType')) return withStatus('monthlyLimit', status, message, payload);
            return withStatus('limitReached', status, message);
        case 400:
            if (has('nodeIds')) return withStatus('invalidNodeId', status, message, payload);
            return withStatus('badRequest', status, message);
        case 422:
            return withStatus('validation', status, message, undefined, body?.details);
        case 404:
            return withStatus('notFound', status, message);
        case 502:
        case 503:
            return withStatus('unavailable', status, message);
        default:
            return withStatus('unknown', status, message);
    }
};

// ---------------------------------------------------------------------------------------------
// Bulk upload page: file rules, step resolution, menu name form
// ---------------------------------------------------------------------------------------------

export const MENU_IMPORT_PDF_TYPE: _MenuImportMimeType = 'application/pdf';
export const MENU_IMPORT_IMAGE_TYPES: _MenuImportMimeType[] = ['image/png', 'image/jpeg', 'image/webp'];
export const MENU_IMPORT_MAX_IMAGES = 10;
export const MENU_IMPORT_MAX_FILE_BYTES = 25165824;
export const MENU_IMPORT_MENU_NAME_MAX = 100;
export const MENU_IMPORT_FILE_NAME_MAX = 255;
export const MENU_IMPORT_PRICE_MAX = 10;
export const MENU_IMPORT_ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp';

const MENU_IMPORT_EXTENSION_TYPES: Record<string, _MenuImportMimeType> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    webp: 'image/webp',
};

export type _ImportFileError = 'mixedTypes' | 'tooManyPdfs' | 'tooManyImages' | 'unsupportedType' | 'tooLarge' | 'empty' | 'nameTooLong';

export type _ImportStep = 'upload' | 'extracting' | 'failed' | 'ready' | 'adding';

const isImportMimeType = (value: string): value is _MenuImportMimeType =>
    value === MENU_IMPORT_PDF_TYPE || (MENU_IMPORT_IMAGE_TYPES as string[]).includes(value);

/** `file.type` when it is one of the four accepted types, else derived from the extension, else null. */
export const getImportMimeType = (file: File): _MenuImportMimeType | null => {
    if (isImportMimeType(file.type)) return file.type;
    const extension = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() : undefined;
    return (extension && MENU_IMPORT_EXTENSION_TYPES[extension]) || null;
};

export type _ImportFileKind = 'pdf' | 'image' | 'other';

/** What a file is for display: from its mime type when it is one of the accepted four, else from the extension in its name. */
export const getImportFileKind = (fileName: string, mimeType?: string): _ImportFileKind => {
    const extension = fileName.includes('.') ? fileName.split('.').pop()?.toLowerCase() : undefined;
    const type = mimeType && isImportMimeType(mimeType) ? mimeType : extension && MENU_IMPORT_EXTENSION_TYPES[extension];
    if (type === MENU_IMPORT_PDF_TYPE) return 'pdf';
    return type ? 'image' : 'other';
};

export const validateImportFiles = (files: File[]): { files: File[]; error: _ImportFileError | null } => {
    let pdfs = 0;
    let images = 0;
    for (const file of files) {
        const mimeType = getImportMimeType(file);
        if (!mimeType) return { files, error: 'unsupportedType' };
        if (file.name.length > MENU_IMPORT_FILE_NAME_MAX) return { files, error: 'nameTooLong' };
        if (file.size < 1) return { files, error: 'empty' };
        if (file.size > MENU_IMPORT_MAX_FILE_BYTES) return { files, error: 'tooLarge' };
        if (mimeType === MENU_IMPORT_PDF_TYPE) pdfs += 1;
        else images += 1;
    }
    if (pdfs > 0 && images > 0) return { files, error: 'mixedTypes' };
    if (pdfs > 1) return { files, error: 'tooManyPdfs' };
    if (images > MENU_IMPORT_MAX_IMAGES) return { files, error: 'tooManyImages' };
    return { files, error: null };
};

export const getImportStep = (status?: _MenuImportStatus): _ImportStep => {
    switch (status) {
        case 'UPLOADED':
        case 'SPLITTING':
        case 'EXTRACTING':
        case 'CONSOLIDATING':
            return 'extracting';
        case 'FAILED':
            return 'failed';
        case 'APPROVED':
        case 'COMMITTING':
            return 'adding';
        case 'REVIEW_PENDING':
        case 'PARTIALLY_COMMITTED':
        case 'PARTIALLY_PUBLISHED':
            return 'ready';
        default:
            return 'upload';
    }
};

// Rows are newest first. A FAILED batch cannot be cancelled, so it stays in the list for good: it is open only while it is the
// newest row, since any newer batch means the user has moved on.
export const findOpenBatch = (rows: _MenuImportBatchSummary[]): _MenuImportBatchSummary | undefined =>
    rows.find((row, index) => (row.status === 'FAILED' ? index === 0 : row.status !== 'COMPLETED' && row.status !== 'CANCELLED'));

export type _MenuNameForm = { menuName: string };

export const initialMenuNameForm: _MenuNameForm = { menuName: '' };

export const menuNameSchema = Yup.object({
    menuName: Yup.string().trim().required().max(MENU_IMPORT_MENU_NAME_MAX),
});

// ---------------------------------------------------------------------------------------------
// Review step: row model, pure draft transforms, filter, edit form
// ---------------------------------------------------------------------------------------------

export const MENU_IMPORT_ITEM_NAME_MIN = 2;
export const MENU_IMPORT_ITEM_NAME_MAX = 100;
export const MENU_IMPORT_ITEM_DESCRIPTION_MAX = 500;
export const MENU_IMPORT_PRICE_PATTERN = /^\d+(\.\d{1,2})?$/;

export type _ReviewTab = 'all' | 'needsReview' | 'ready' | 'added';

export type _ReviewRow = {
    nodeId: string;
    menuNodeId: string;
    categoryNodeId: string;
    categoryName: string;
    itemName: string;
    itemDescription: string;
    defaultPrice: string;
    confidence: _DraftItem['confidence'];
    needsReview: boolean;
    committed: boolean;
};

export type _ReviewItemForm = {
    itemName: string;
    itemDescription: string;
    defaultPrice: string;
};

export const initialReviewItemForm: _ReviewItemForm = { itemName: '', itemDescription: '', defaultPrice: '' };

export const flattenDraft = (tree: _DraftTree | null | undefined): _ReviewRow[] =>
    (tree?.menus ?? []).flatMap((menu) =>
        menu.categories.flatMap((category) =>
            category.items.map((item) => {
                const defaultPrice = item.itemPrices.find((price) => price.isDefault);
                return {
                    nodeId: item.nodeId ?? '',
                    menuNodeId: menu.nodeId ?? '',
                    categoryNodeId: category.nodeId ?? '',
                    categoryName: category.categoryName,
                    itemName: item.itemName,
                    itemDescription: item.itemDescription ?? '',
                    defaultPrice: defaultPrice === undefined ? '' : String(defaultPrice.fieldValue),
                    confidence: item.confidence,
                    needsReview: item.confidence !== 'high',
                    committed: item.committed === true,
                };
            })
        )
    );

const mapItems = (tree: _DraftTree, change: (item: _DraftItem) => _DraftItem): _DraftTree => ({
    ...tree,
    menus: tree.menus.map((menu) => ({
        ...menu,
        categories: menu.categories.map((category) => ({ ...category, items: category.items.map(change) })),
    })),
});

const isTargeted = (item: _DraftItem, ids: ReadonlySet<string>) => item.nodeId !== undefined && ids.has(item.nodeId);

export const confirmItems = (tree: _DraftTree, nodeIds: readonly string[]): _DraftTree => {
    const ids = new Set(nodeIds);
    return mapItems(tree, (item) => (isTargeted(item, ids) ? { ...item, confidence: 'high' } : item));
};

/** The reverse of `confirmItems`: puts the given items back to Needs review (`medium` confidence). */
export const markForReview = (tree: _DraftTree, nodeIds: readonly string[]): _DraftTree => {
    const ids = new Set(nodeIds);
    return mapItems(tree, (item) => (isTargeted(item, ids) ? { ...item, confidence: 'medium' } : item));
};

/**
 * `PUT …/draft` answers with the draft minus the server-managed `committed` and `entityId` fields, which only the batch detail
 * carries. A draft save cannot change them, so they are copied back by `nodeId` from the tree that was sent; a value the
 * response does carry wins. Without this an added item reads as Ready after every save until the page reloads.
 */
export const keepServerFields = (sent: _DraftTree, saved: _DraftTree): _DraftTree => {
    const sentNodes = new Map<string, _DraftNodeMeta>();
    const remember = (node: _DraftNodeMeta) => node.nodeId !== undefined && sentNodes.set(node.nodeId, node);
    sent.menus.forEach((menu) => {
        remember(menu);
        menu.categories.forEach((category) => {
            remember(category);
            category.items.forEach(remember);
        });
    });
    const restore = <T extends _DraftNodeMeta>(node: T): T => {
        const from = node.nodeId === undefined ? undefined : sentNodes.get(node.nodeId);
        if (!from) return node;
        return {
            ...node,
            ...(node.committed === undefined && from.committed !== undefined ? { committed: from.committed } : {}),
            ...(node.entityId === undefined && from.entityId !== undefined ? { entityId: from.entityId } : {}),
        };
    };
    return {
        ...saved,
        menus: saved.menus.map((menu) =>
            restore({
                ...menu,
                categories: menu.categories.map((category) => restore({ ...category, items: category.items.map(restore) })),
            })
        ),
    };
};

/**
 * Writes the edited price to the item's default price. The API needs exactly one default, so the first price becomes the default when
 * none is, and an item with no prices gets one. An unchanged value keeps its original `fieldValue` (and type); a changed one is a string.
 */
const withDefaultPrice = (prices: _DraftPrice[], value: string): _DraftPrice[] => {
    if (prices.length === 0) return [{ fieldName: '', fieldValue: value, isDefault: true }];
    const defaultIndex = Math.max(
        prices.findIndex((price) => price.isDefault),
        0
    );
    return prices.map((price, index) =>
        index === defaultIndex
            ? { ...price, isDefault: true, fieldValue: String(price.fieldValue) === value ? price.fieldValue : value }
            : price
    );
};

export const editItem = (tree: _DraftTree, nodeId: string, values: _ReviewItemForm): _DraftTree =>
    mapItems(tree, (item) => {
        if (item.nodeId !== nodeId) return item;
        return {
            ...item,
            itemName: values.itemName,
            itemDescription: values.itemDescription,
            itemPrices: withDefaultPrice(item.itemPrices, values.defaultPrice),
            confidence: 'high',
        };
    });

/** Removes the items, then any category left without items, then any menu left without categories. */
export const removeItems = (tree: _DraftTree, nodeIds: readonly string[]): _DraftTree => {
    const ids = new Set(nodeIds);
    return {
        ...tree,
        menus: tree.menus
            .map((menu) => ({
                ...menu,
                categories: menu.categories
                    .map((category) => ({ ...category, items: category.items.filter((item) => !isTargeted(item, ids)) }))
                    .filter((category) => category.items.length > 0),
            }))
            .filter((menu) => menu.categories.length > 0),
    };
};

/**
 * Sets the `selected` flags the publish call reads: an uncommitted item is selected when its id is checked; a category or menu is
 * selected only when one of its uncommitted items is. Committed nodes keep their flags, because the server ignores them.
 */
export const applySelection = (tree: _DraftTree, checkedIds: ReadonlySet<string> | readonly string[]): _DraftTree => {
    const checked = new Set(checkedIds);
    return {
        ...tree,
        menus: tree.menus.map((menu) => {
            const categories = menu.categories.map((category) => {
                const items = category.items.map((item) =>
                    item.committed ? item : { ...item, selected: item.nodeId !== undefined && checked.has(item.nodeId) }
                );
                const anySelected = items.some((item) => !item.committed && item.selected === true);
                return category.committed ? { ...category, items } : { ...category, items, selected: anySelected };
            });
            const anySelected = categories.some((category) => category.items.some((item) => !item.committed && item.selected === true));
            return menu.committed ? { ...menu, categories } : { ...menu, categories, selected: anySelected };
        }),
    };
};

export type _PublishOutcome = 'completed' | 'partial' | 'commitFailed' | 'nothingAdded';

/** What a batch that has just left COMMITTING means for the admin; undefined while it is not at a publish outcome. */
export const getPublishOutcome = (status?: _MenuImportStatus): _PublishOutcome | undefined => {
    switch (status) {
        case 'COMPLETED':
            return 'completed';
        case 'PARTIALLY_PUBLISHED':
            return 'partial';
        case 'PARTIALLY_COMMITTED':
            return 'commitFailed';
        case 'REVIEW_PENDING':
            return 'nothingAdded';
        default:
            return undefined;
    }
};

export const filterReviewRows = (rows: _ReviewRow[], tab: _ReviewTab, search: string): _ReviewRow[] => {
    const needle = search.trim().toLowerCase();
    return rows.filter((row) => {
        if (tab === 'needsReview' && !row.needsReview) return false;
        if (tab === 'ready' && row.needsReview) return false;
        if (tab === 'added' && !row.committed) return false;
        if (!needle) return true;
        return [row.itemName, row.itemDescription, row.categoryName].some((text) => text.toLowerCase().includes(needle));
    });
};

export const reviewItemSchema = Yup.object({
    itemName: Yup.string().trim().required().min(MENU_IMPORT_ITEM_NAME_MIN).max(MENU_IMPORT_ITEM_NAME_MAX),
    itemDescription: Yup.string().max(MENU_IMPORT_ITEM_DESCRIPTION_MAX),
    defaultPrice: Yup.string().required().max(MENU_IMPORT_PRICE_MAX).matches(MENU_IMPORT_PRICE_PATTERN),
});

export const convertRowToReviewItemForm = (row: _ReviewRow): _ReviewItemForm => ({
    itemName: row.itemName,
    itemDescription: row.itemDescription,
    defaultPrice: row.defaultPrice,
});
